import { useYearPeriodStore } from '../../store/useYearPeriodStore'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { YearPeriodPicker } from './shared/YearPeriodPicker'
import { DualGrid } from './shared/DualGrid'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { useDlyOneYearData } from '../../hooks/useDlyOneYearData'
import { SNOW_SCALE, SNOW_NEW_SCALE } from './shared/weatherUtils'

const SLUG = 'dly-one-year-snow'

function snowCellStyle(value) {
  if (value == null || value < 0.1) return null
  const entry = SNOW_SCALE.find(e => value >= e.min)
  return entry ? { bg: entry.bg, text: entry.text } : null
}

function snowNewCellStyle(value) {
  if (value == null || value < 0.1) return null
  const entry = SNOW_NEW_SCALE.find(e => value >= e.min)
  return entry ? { bg: entry.bg, text: entry.text } : null
}

function snowFormat(value) {
  if (value == null) return '—'
  if (value === 0) return '0'
  return String(Math.round(value))
}

function SnowLegend({ scale, title, zeroLabel }) {
  return (
    <div className="shrink-0 flex flex-col gap-0.5">
      <div className="text-xs font-semibold text-gray-500 mb-0.5">{title}</div>
      <div className="text-xs font-semibold text-gray-400 tracking-wide mb-1">cm</div>
      {scale.map(({ label, bg }) => (
        <div key={label} className="flex items-center gap-1.5 text-xs whitespace-nowrap">
          <span className="inline-block w-4 h-4 rounded-sm shrink-0" style={{ backgroundColor: bg }} />
          <span className="text-gray-600">{label}</span>
        </div>
      ))}
      <div className="flex items-center gap-1.5 text-xs whitespace-nowrap mt-0.5">
        <span className="inline-block w-4 h-4 rounded-sm shrink-0 border border-gray-200" />
        <span className="text-gray-400">{zeroLabel}</span>
      </div>
    </div>
  )
}

export function DlyOneYearSnowView({ station }) {
  const { year, setYear } = useYearPeriodStore()

  const orientation = useTableOrientationStore((s) => s.getOrientation(SLUG))

  const { dataMap: snowMap,    isLoading: l1, isError: e1 } = useDlyOneYearData(station?.gh_id, year, 'DLY_SNOW')
  const { dataMap: snowNewMap, isLoading: l2, isError: e2 } = useDlyOneYearData(station?.gh_id, year, 'DLY_SNOWNEW')

  const isLoading = l1 || l2
  const isError   = e1 || e2

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}
        <div className="flex items-center justify-between gap-3">
          <YearPeriodPicker year={year} onYearChange={setYear} obsType="DLY" />
          <TableOrientationToggle slug={SLUG} />
        </div>
      </div>

      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Spinner size="sm" /> Načítání dat…
          </div>
        )}
        {isError && <p className="text-red-500 text-sm">Chyba při načítání dat</p>}
        {!isLoading && !isError && (
          <div className="flex-1 min-h-0 flex gap-6">
            <div className="flex-1 min-w-0 min-h-0">
              <DualGrid
                year={year}
                orientation={orientation}
                left={{  dataMap: snowMap,    getCellStyle: snowCellStyle,    formatCell: snowFormat, title: 'Celkový sníh' }}
                right={{ dataMap: snowNewMap, getCellStyle: snowNewCellStyle, formatCell: snowFormat, title: 'Nový sníh' }}
              />
            </div>
            <div className="shrink-0 flex flex-col gap-6">
              <SnowLegend scale={SNOW_SCALE}     title="Celkový sníh" zeroLabel="= 0 cm" />
              <SnowLegend scale={SNOW_NEW_SCALE} title="Nový sníh"    zeroLabel="= 0 cm" />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
